import { computed, Directive, input } from '@angular/core';
import { mergeClasses } from '@wheere-front/shared-util';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ClassValue } from 'clsx';

export const buttonVariants = cva(
  'inline-flex items-center whitespace-nowrap justify-center gap-1.5 rounded-md flex-nowrap font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed ring-offset-background',
  {
    variants: {
      variant: {
        default: 'bg-primary-500 text-white hover:bg-primary-600',
        secondary: 'bg-secondary-500 text-white hover:bg-secondary-600',
        destructive: 'bg-red-500 text-white hover:bg-red-600',
        // Brand accents — mirror the domain colours the integrator uses inline (sites =
        // electric purple, gateways = warm yellow, team = aquamarine). Token is `eletricPurple`
        // (repo spelling); the variant name is the corrected English.
        electricPurple: 'bg-eletricPurple-500 text-white hover:bg-eletricPurple-600',
        warmYellow: 'bg-warmYellow-500 text-gray-900 hover:bg-warmYellow-600',
        aquamarine: 'bg-aquamarine-500 text-white hover:bg-aquamarine-600',
        outline:
          'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600',
        // Transparent / borderless (integrator's many ghost buttons: cancel, steppers, pagers).
        ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700',
        // Red-outlined destructive (Clean / Delete / Detach on the integrator detail page).
        destructiveOutline:
          'border border-red-300 bg-transparent text-red-600 hover:bg-red-50 dark:border-red-500/40 dark:text-red-400 dark:hover:bg-red-500/10',
        icon: 'bg-transparent text-sm text-gray-400 hover:text-gray-900 dark:text-slate-400 dark:hover:text-slate-100',
      },
      size: {
        xxs: 'h-5 px-2 text-xs',
        xs: 'h-8 px-2.5 text-xs',
        sm: 'h-9 px-3 text-sm',
        default: 'h-9 px-3 text-sm',
        lg: 'h-11 px-5 text-sm',
      },
    },
    compoundVariants: [
      {
        variant: 'icon',
        size: 'default',
        class: 'p-2',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export type ButtonVariants = VariantProps<typeof buttonVariants>;

@Directive({
  selector: '[libBtn], [libButton]',
  standalone: true,
  host: {
    '[class]': '_computedClass()',
  },
})
export class ButtonDirective {
  readonly userClass = input<ClassValue>('');

  readonly variant = input<ButtonVariants['variant']>('default');
  readonly size = input<ButtonVariants['size']>('default');

  protected _computedClass = computed(() =>
    mergeClasses(
      buttonVariants({
        variant: this.variant(),
        size: this.size(),
      }),
      this.userClass()
    )
  );
}
